import React from 'react';
import { ui } from '../../config/uiSizes';
import { useToast } from '../Toast/ToastProvider';
import { Tooltip } from '../Tooltip';
import { runDryRunAgain } from '../../services/Migration/runDryRunAgainService';
import { logger } from '../../utils/logger';

interface RunDryRunAgainButtonProps {
    /** Bearer token for the API calls. */
    token: string;
    /** Migration whose dry-run preview should be re-executed. */
    migrationId: string;
    /** Called after the dry run is successfully re-queued (e.g. to close the modal). */
    onQueued?: (() => void) | undefined;
    /** Optional extra classes for the outer container. */
    className?: string;
}

/**
 * Re-executes the dry-run preview on the same migration. Unlike "Re-run preview" (which only refetches
 * the cached report), this re-runs the plan and the live target-org readiness checks — so after the
 * operator fixes a prerequisite (e.g. frees up an emergency number), the confidence score updates. A
 * dry run applies nothing, so no destructive confirmation is needed.
 */
const RunDryRunAgainButton: React.FC<RunDryRunAgainButtonProps> = ({
    token,
    migrationId,
    onQueued,
    className = ''
}) => {
    const { showToast } = useToast();
    const [submitting, setSubmitting] = React.useState(false);

    const handleClick = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await runDryRunAgain(token, migrationId);
            showToast('Dry run re-queued — this can take a few minutes.', 'success');
            onQueued?.();
        } catch (err) {
            logger.error('Run dry run again failed', {
                error: err instanceof Error ? err.message : String(err),
                migrationId
            });
            showToast('Failed to re-queue the dry run. Please try again.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={`inline-flex items-center ${ui.gap.xs} ${className}`}>
            <button
                type="button"
                disabled={submitting}
                onClick={() => void handleClick()}
                className={`${ui.button.lg} bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 font-semibold rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-all shadow-md hover:shadow-lg flex items-center justify-center ${ui.gap.sm} ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
                <svg className={ui.svg.sm} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path
                        fillRule="evenodd"
                        d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                        clipRule="evenodd"
                    />
                </svg>
                <span>{submitting ? 'Re-queuing…' : 'Run dry run again'}</span>
            </button>
            <Tooltip
                position="top"
                content="Re-runs the preview against the target org (nothing is applied). Use after fixing a prerequisite to refresh the confidence score."
            />
        </div>
    );
};

export default RunDryRunAgainButton;
